import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable form component for lottery statistics parameters
 * Handles input validation and form submission
 *
 * @param {object} props - Component props
 * @param {number} props.topN - Number of top numbers to display
 * @param {number} props.minFrequentMatch - Minimum frequent matches
 * @param {number} props.minPreferredMatch - Minimum preferred matches
 * @param {string} props.preferredNum - Comma-separated preferred numbers
 * @param {function} props.onTopNChange - Handler for topN change
 * @param {function} props.onMinFrequentMatchChange - Handler for minFrequentMatch change
 * @param {function} props.onMinPreferredMatchChange - Handler for minPreferredMatch change
 * @param {function} props.onPreferredNumChange - Handler for preferredNum change
 * @param {function} props.onSubmit - Handler for form submission
 * @param {function} props.onReset - Handler for form reset
 * @param {boolean} props.loading - Loading state
 */
const LotteryStatsForm = ({
  topN = 5,
  minFrequentMatch = 3,
  minPreferredMatch = 1,
  preferredNum = '7,12',
  onTopNChange,
  onMinFrequentMatchChange,
  onMinPreferredMatchChange,
  onPreferredNumChange,
  onSubmit,
  onReset,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="mb-4">
      <div className="row">
        <div className="col-md-3">
          <div className="mb-3">
            <label htmlFor="topN" className="form-label">
              {t('topNLabel')}
            </label>
            <input
              type="number"
              id="topN"
              className="form-control"
              value={topN}
              onChange={(e) => onTopNChange(Number(e.target.value))}
              min="1"
              max="90"
              required
              disabled={loading}
            />
            <small className="text-muted">{t('topNHelp')}</small>
          </div>
        </div>

        <div className="col-md-3">
          <div className="mb-3">
            <label htmlFor="minFrequentMatch" className="form-label">
              {t('minFrequentMatchLabel')}
            </label>
            <input
              type="number"
              id="minFrequentMatch"
              className="form-control"
              value={minFrequentMatch}
              onChange={(e) => onMinFrequentMatchChange(Number(e.target.value))}
              min="1"
              max="20"
              required
              disabled={loading}
            />
            <small className="text-muted">{t('minFrequentMatchHelp')}</small>
          </div>
        </div>

        <div className="col-md-3">
          <div className="mb-3">
            <label htmlFor="minPreferredMatch" className="form-label">
              {t('minPreferredMatchLabel')}
            </label>
            <input
              type="number"
              id="minPreferredMatch"
              className="form-control"
              value={minPreferredMatch}
              onChange={(e) => onMinPreferredMatchChange(Number(e.target.value))}
              min="0"
              max="10"
              required
              disabled={loading}
            />
            <small className="text-muted">{t('minPreferredMatchHelp')}</small>
          </div>
        </div>

        <div className="col-md-3">
          <div className="mb-3">
            <label htmlFor="preferredNum" className="form-label">
              {t('preferredNumLabel')}
            </label>
            <input
              type="text"
              id="preferredNum"
              className="form-control"
              value={preferredNum}
              onChange={onPreferredNumChange}
              placeholder={t('preferredNumPlaceholder')}
              disabled={minPreferredMatch === 0 || loading}
            />
            <small className="text-muted">{t('preferredNumHelp')}</small>
          </div>
        </div>
      </div>

      <div className="d-flex gap-2">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('loading')}...
            </>
          ) : (
            t('submit')
          )}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onReset}
          disabled={loading}
          title={t('reset')}
        >
          ↻ {t('reset')}
        </button>
      </div>
    </form>
  );
};

export default LotteryStatsForm;
